import { useCallback } from 'react';
import useParkedQuestion from '~/hooks/useParkedQuestion';

/**
 * A retrieval that came up short, awaiting the user's decision. Counts are
 * files, not datasets.
 */
export type IncompleteRetrievalState = {
    open: boolean;
    retrieved: number;
    missing: number;
};

export type IncompleteRetrievalDecision = {
    /** What the confirmation dialog shows. */
    state: IncompleteRetrievalState;
    /**
     * Asks whether to go on with a short retrieval, and resolves with the
     * answer. A question that can no longer be answered resolves `false`.
     */
    awaitDecision: (retrieved: number, missing: number) => Promise<boolean>;
    /** Answers the question currently asked; a no-op when none is. */
    settle: (proceed: boolean) => void;
};

type IncompleteRetrieval = Omit<IncompleteRetrievalState, 'open'>;

const NO_INCOMPLETE_RETRIEVAL: IncompleteRetrieval = {
    retrieved: 0,
    missing: 0,
};

/**
 * A parked yes/no on a short retrieval.
 *
 * A short retrieval is a clinical decision, not a notification: the parser
 * interpolates across gaps, so missing files would reappear as synthesized
 * anatomy. Whatever loads the files waits on this before committing any of
 * them, so nothing synthesized reaches the session without the user saying
 * so (SR-WOR-H-37-1).
 */
export function useIncompleteRetrievalDecision(): IncompleteRetrievalDecision {
    const { question, ask, answer } = useParkedQuestion<
        IncompleteRetrieval,
        boolean
    >(NO_INCOMPLETE_RETRIEVAL, false);

    const awaitDecision = useCallback(
        (retrieved: number, missing: number): Promise<boolean> =>
            ask({ retrieved, missing }),
        [ask]
    );

    return { state: question, awaitDecision, settle: answer };
}
